/**
 * oddit-section-main-collection
 * Interactive behaviors: filter drawer, sort dropdown, accordion filters, load more,
 * product swatches (delegated — works with Load more appends)
 */
(function () {
  'use strict';

  /* Card-level behaviors (carousel, swatches, quick-add, lazy video) are now
     handled globally by oddit-collection-product-card.js */

  function initSection(sectionEl) {
    if (!sectionEl) return;

    var section = sectionEl;

    /* ====================================================================
       Filter Drawer
       ==================================================================== */
    var filterBtn = section.querySelector('.oddit-section-main-collection__filter-btn');
    var drawer = section.querySelector('.oddit-section-main-collection__filter-drawer');
    var drawerOverlay = section.querySelector('.oddit-section-main-collection__drawer-overlay');
    var drawerClose = section.querySelector('.oddit-section-main-collection__drawer-close');

    function openDrawer() {
      if (!drawer || !drawerOverlay) return;
      var scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      document.body.style.paddingRight = scrollbarWidth + 'px';
      drawer.classList.add('oddit-section-main-collection__filter-drawer--open');
      drawerOverlay.classList.add('oddit-section-main-collection__drawer-overlay--active');
      document.body.style.overflow = 'hidden';
      if (drawerClose) drawerClose.focus();
    }

    function closeDrawer() {
      if (!drawer || !drawerOverlay) return;
      drawer.classList.remove('oddit-section-main-collection__filter-drawer--open');
      drawerOverlay.classList.remove('oddit-section-main-collection__drawer-overlay--active');
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      if (filterBtn) filterBtn.focus();
    }

    if (filterBtn) {
      filterBtn.addEventListener('click', openDrawer);
    }

    if (drawerClose) {
      drawerClose.addEventListener('click', closeDrawer);
    }

    if (drawerOverlay) {
      drawerOverlay.addEventListener('click', closeDrawer);
    }

    // Close drawer on Escape
    function handleKeydown(e) {
      if (e.key === 'Escape' && drawer && drawer.classList.contains('oddit-section-main-collection__filter-drawer--open')) {
        closeDrawer();
      }
    }
    document.addEventListener('keydown', handleKeydown);

    /* ====================================================================
       Filter Accordion Groups
       ==================================================================== */
    var filterHeaders = section.querySelectorAll('.oddit-section-main-collection__filter-group-header');

    filterHeaders.forEach(function (header) {
      header.addEventListener('click', function () {
        var group = header.closest('.oddit-section-main-collection__filter-group');
        var content = group.querySelector('.oddit-section-main-collection__filter-group-content');
        var isExpanded = header.getAttribute('aria-expanded') === 'true';

        if (isExpanded) {
          header.setAttribute('aria-expanded', 'false');
          content.setAttribute('hidden', '');
          group.setAttribute('data-open', 'false');
        } else {
          header.setAttribute('aria-expanded', 'true');
          content.removeAttribute('hidden');
          group.setAttribute('data-open', 'true');
        }
      });
    });

    /* ====================================================================
       Filter Form — live count update
       ==================================================================== */
    var filterForm = section.querySelector('[data-filter-form]');
    var drawerCta = section.querySelector('.oddit-section-main-collection__drawer-cta');

    if (filterForm && drawerCta) {
      var countFetchTimeout;

      filterForm.addEventListener('change', function () {
        clearTimeout(countFetchTimeout);
        drawerCta.textContent = 'Updating\u2026';

        countFetchTimeout = setTimeout(function () {
          var params = new URLSearchParams(new FormData(filterForm));
          params.set('section_id', section.id);

          fetch(filterForm.action + '?' + params.toString())
            .then(function (r) { return r.text(); })
            .then(function (html) {
              var doc = new DOMParser().parseFromString(html, 'text/html');
              var updatedCta = doc.querySelector('.oddit-section-main-collection__drawer-cta');
              drawerCta.textContent = updatedCta ? updatedCta.textContent : 'See results';
            })
            .catch(function () {
              drawerCta.textContent = 'See results';
            });
        }, 400);
      });
    }

    /* ====================================================================
       Sort Dropdown
       ==================================================================== */
    var sortTrigger = section.querySelector('.oddit-section-main-collection__sort-trigger');
    var sortDropdown = section.querySelector('.oddit-section-main-collection__sort-dropdown');
    var sortValueEl = section.querySelector('.oddit-section-main-collection__sort-value');

    function openSortDropdown() {
      if (!sortTrigger || !sortDropdown) return;
      sortTrigger.setAttribute('aria-expanded', 'true');
      sortDropdown.removeAttribute('hidden');
    }

    function closeSortDropdown() {
      if (!sortTrigger || !sortDropdown) return;
      sortTrigger.setAttribute('aria-expanded', 'false');
      sortDropdown.setAttribute('hidden', '');
    }

    if (sortTrigger && sortDropdown) {
      sortTrigger.addEventListener('click', function (e) {
        e.stopPropagation();
        var isOpen = sortTrigger.getAttribute('aria-expanded') === 'true';
        if (isOpen) {
          closeSortDropdown();
        } else {
          openSortDropdown();
        }
      });

      sortDropdown.querySelectorAll('li').forEach(function (option) {
        option.addEventListener('click', function () {
          var sortValue = option.getAttribute('data-value');

          // Update selected state
          sortDropdown.querySelectorAll('li').forEach(function (li) {
            li.setAttribute('aria-selected', 'false');
          });
          option.setAttribute('aria-selected', 'true');

          // Update displayed value
          if (sortValueEl) {
            sortValueEl.textContent = option.textContent.trim();
          }

          closeSortDropdown();

          // Navigate to sorted URL
          var url = new URL(window.location.href);
          url.searchParams.set('sort_by', sortValue);
          window.location.assign(url.toString());
        });
      });

      // Close on click outside
      function handleClickOutside(e) {
        if (!sortTrigger.contains(e.target) && !sortDropdown.contains(e.target)) {
          closeSortDropdown();
        }
      }
      document.addEventListener('click', handleClickOutside);
    }

    /* ====================================================================
       Load More / Infinite Scroll
       Click handling is delegated to the section so the listener survives
       the pagination block being re-rendered after each load.
       ==================================================================== */
    var paginationType = section.getAttribute('data-pagination-type') || 'load_more';
    var loadMoreObserver = null;
    var loadingMore = false;
    var prefetch = null;

    function fetchPageHtml(url) {
      return fetch(url).then(function (response) { return response.text(); });
    }

    // Start (or reuse) a background fetch of whatever the current load-more
    // button points to, so the response is ready before it's needed. Only in
    // infinite mode — in button mode we wait for an explicit click.
    function prefetchNext() {
      if (paginationType !== 'infinite') return;
      var btn = section.querySelector('.oddit-section-main-collection__load-more');
      var url = btn && btn.getAttribute('href');
      if (!url) { prefetch = null; return; }
      if (prefetch && prefetch.url === url) return; // already buffered/in-flight
      var promise = fetchPageHtml(url);
      promise.catch(function () {}); // swallow if it's never consumed (no scroll)
      prefetch = { url: url, promise: promise };
    }

    // Append the fetched page's cards and refresh the pagination block.
    function applyNextPage(html) {
      var doc = new DOMParser().parseFromString(html, 'text/html');
      var newGrid = doc.querySelector('.oddit-section-main-collection__grid');
      var currentGrid = section.querySelector('.oddit-section-main-collection__grid');
      if (newGrid && currentGrid) {
        while (newGrid.children.length > 0) {
          currentGrid.appendChild(newGrid.children[0]);
        }
      }
      var newPagination = doc.querySelector('.oddit-section-main-collection__pagination');
      var currentPagination = section.querySelector('.oddit-section-main-collection__pagination');
      if (currentPagination && newPagination) {
        currentPagination.innerHTML = newPagination.innerHTML;
      } else if (currentPagination && !newPagination) {
        currentPagination.remove();
      }
      // Re-init carousels and lazy videos for new cards
      if (window.odditInitProductCards) window.odditInitProductCards(section);
    }

    function loadMore(loadMoreBtn) {
      if (loadingMore) return;
      var nextUrl = loadMoreBtn.getAttribute('href');
      if (!nextUrl) return;
      loadingMore = true;

      loadMoreBtn.textContent = 'Loading...';
      loadMoreBtn.style.pointerEvents = 'none';

      // Reuse the prefetched response when it matches; otherwise fetch now.
      var usingPrefetch = prefetch && prefetch.url === nextUrl;
      var source = usingPrefetch ? prefetch.promise : fetchPageHtml(nextUrl);
      prefetch = null;

      function done(html) {
        applyNextPage(html);
        loadingMore = false;
        observeLoadMore(); // re-arm on the new button
        prefetchNext(); // immediately buffer the following page
      }

      function fail() {
        loadMoreBtn.textContent = 'Error. Try again.';
        loadMoreBtn.style.pointerEvents = '';
        loadingMore = false;
      }

      source
        .then(done)
        .catch(function () {
          // A stale/failed prefetch shouldn't surface as an error — retry once
          // with a fresh fetch before giving up.
          if (usingPrefetch) {
            fetchPageHtml(nextUrl).then(done).catch(fail);
          } else {
            fail();
          }
        });
    }

    section.addEventListener('click', function (e) {
      var loadMoreBtn = e.target.closest('.oddit-section-main-collection__load-more');
      if (!loadMoreBtn) return;
      e.preventDefault();
      loadMore(loadMoreBtn);
    });

    // Infinite scroll: trigger the next load as the button nears the viewport.
    function observeLoadMore() {
      if (paginationType !== 'infinite' || !('IntersectionObserver' in window)) return;
      var btn = section.querySelector('.oddit-section-main-collection__load-more');
      if (!btn) return;
      if (loadMoreObserver) loadMoreObserver.disconnect();
      loadMoreObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            loadMoreObserver.disconnect();
            loadMore(btn);
          }
        });
      }, { rootMargin: '1200px 0px' });
      loadMoreObserver.observe(btn);
    }

    observeLoadMore();
    prefetchNext(); // buffer page 2 right away so the first load is instant

    /* ====================================================================
       Mobile Image Swipe Carousel — handled by global oddit-collection-product-card.js
       ==================================================================== */

    // Store cleanup function
    section._cleanupSection = function () {
      document.removeEventListener('keydown', handleKeydown);
      if (typeof handleClickOutside === 'function') {
        document.removeEventListener('click', handleClickOutside);
      }
      if (loadMoreObserver) loadMoreObserver.disconnect();
    };
  }

  // Initialize on DOMContentLoaded
  var section = document.querySelector('.oddit-section-main-collection');
  if (section) {
    initSection(section);
  }

  (function setupSortPortal() {
    var sortOnlyToolbar = document.querySelector('.oddit-section-main-collection__toolbar--sort-only');
    if (!sortOnlyToolbar) return;
    var sortEl = sortOnlyToolbar.querySelector('.oddit-section-main-collection__sort');
    var titleGroup = document.querySelector('.oddit-section-collection-header__title-group');
    if (!sortEl || !titleGroup) return;

    titleGroup.appendChild(sortEl);
    titleGroup.classList.add('has-portal-sort');
    // Mark the empty toolbar so CSS can collapse it deterministically
    // (the :empty selector won't match if Liquid leaves any whitespace).
    sortOnlyToolbar.classList.add('is-portaled');
  })();

  // Theme Editor support
  document.addEventListener('shopify:section:load', function (e) {
    var newSection = e.target.querySelector('.oddit-section-main-collection');
    if (newSection) {
      initSection(newSection);
    }
  });

  document.addEventListener('shopify:section:unload', function (e) {
    var oldSection = e.target.querySelector('.oddit-section-main-collection');
    if (oldSection && typeof oldSection._cleanupSection === 'function') {
      oldSection._cleanupSection();
    }
  });
})();
